import { useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';
import { useDoctorSearch } from '../../../api/use-doctor-search';
import { FadeInOut } from '../../../components/fade-in-out';
import { ProgressLayout } from '../../../components/layouts/progress-layout';
import { PatchInviteForm } from '../../../components/patch-invite-form';
import { Autocomplete, makeAutocompleteHook } from '../../../components/ui/autocomplete';
import { EditCard } from '../../../components/ui/edit-card';
import { Input } from '../../../components/ui/input';

export const schema = z.object({
  gp: z.string().nonempty(),
  medicalCentre: z.string(),
});

function extractDefaults({ gp, medicalCentre }, { requestingPractitioner }) {
  return {
    gp: gp ?? requestingPractitioner?.displayName,
    medicalCentre: medicalCentre ?? requestingPractitioner?.organisationNames?.[0],
  };
}

function transformValues(values) {
  return values;
}

export function Doctor(props) {
  return (
    <PatchInviteForm
      extractDefaults={extractDefaults}
      transformValues={transformValues}
      schema={schema}
      nextPath={props.next.path}
    >
      <DoctorInternal {...props} />
    </PatchInviteForm>
  );
}

function DoctorInternal(props) {
  const { t } = useTranslation('page.details');
  const { getValues } = useFormContext();
  const [editing, setEditing] = useState(() => {
    const values = getValues();
    return !(values.gp && values.medicalCentre);
  });

  return (
    <ProgressLayout {...props}>
      <h1 className="title">{t('doctor.title')}</h1>
      <p className="mb-5">{t('doctor.text')}</p>
      <FadeInOut show={editing}>
        <EditDoctor setEditing={setEditing} />
      </FadeInOut>
      <FadeInOut show={!editing}>
        <DoctorDetails setEditing={setEditing} />
      </FadeInOut>
    </ProgressLayout>
  );
}

function useDoctorSearchAutocompleteInternal(query) {
  const state = useDoctorSearch(query);
  const result = useMemo(() => {
    const grouped = {};
    for (const item of state.result ?? []) {
      for (const o of item.organisations?.length > 0
        ? item.organisations
        : [{ id: 'unassociated' }]) {
        if (!Object.hasOwn(grouped, o.id)) {
          grouped[o.id] = { id: o.id, organisation: o, practitioners: [] };
        }
        grouped[o.id].practitioners.push(item);
      }
    }
    return Object.values(grouped);
  }, [state.result]);

  return { ...state, result };
}
const useDoctorSearchAutocomplete = makeAutocompleteHook(useDoctorSearchAutocompleteInternal);

function EditDoctor({ setEditing }) {
  const { t } = useTranslation('page.details');
  const searchState = useDoctorSearchAutocomplete();
  const { setValue } = useFormContext();

  return (
    <div className="space-y-5">
      <Autocomplete
        {...searchState.autocompleteProps}
        label="Search doctor"
        placeholder={t('doctor.searchPlaceholder')}
        name="doctor-suggestions"
        onSelect={(id) => {
          if (id) {
            const [practitionerId, organisationId] = JSON.parse(id);
            const { organisation, practitioners } =
              searchState.result.find((x) => x.id === organisationId) ?? {};
            if (organisation) {
              setValue('medicalCentre', organisation.longDescription, { shouldValidate: true });
              const practitioner = practitioners.find((x) => x.id === practitionerId);
              if (practitioner) {
                setValue('gp', practitioner.nameText, { shouldValidate: true });
              }
            }
            setEditing(false);
            searchState.autocompleteProps.onInputChange('');
          }
        }}
        autoFocus
      >
        {(item) => {
          return (
            <Autocomplete.Section
              id={item.organisation.id}
              title={item.organisation.longDescription}
              items={item.practitioners}
            >
              {(practitioner) => (
                <Autocomplete.Item
                  id={JSON.stringify([practitioner.id, item.organisation.id])}
                  textValue={practitioner.nameText}
                >
                  <span className="before:content-['-'] before:mr-2">{practitioner.nameText}</span>
                </Autocomplete.Item>
              )}
            </Autocomplete.Section>
          );
        }}
      </Autocomplete>
      <Input name="gp" label={t(`doctor.gp`)} />
      <Input name="medicalCentre" label={t(`doctor.medicalCentre`)} />
    </div>
  );
}

function DoctorDetails({ setEditing }) {
  const [gp, medicalCentre] = useWatch({ name: ['gp', 'medicalCentre'] });
  return (
    <EditCard onClick={() => setEditing(true)}>
      <h3 className="title-sm font-semibold mb-2.5">{gp}</h3>
      <span>{medicalCentre}</span>
    </EditCard>
  );
}
